<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header />
        <div class="card-body">
          <div class="mb-5 w-75 d-flex gap-4 flex-column">
            <div class="row mb-2 align-items-center">
              <div class="col-sm-2 d-flex align-items-center">
                <label class="fw-semibold">IDP Period</label>
              </div>

              <div class="col-sm-10 d-flex align-items-center">
                <v-select
                  :options="idpPeriodOptions"
                  v-model="filters.idp_period_id"
                  :reduce="option => option.value"
                  label="text"
                  class="w-50"
                  placeholder="-- Select a period --"
                />
              </div>
            </div>
          </div>
          <Tabs :activeTab="activeTab" @tab-changed="onTabChange" :is-loading="isLoading" />
          <Summary :isLoadingSummary="isLoadingSummary" :summaryData="summaryIdpLearning" />

          <div class="row align-items-center justify-content-between mb-4">
            <div class="col-md-2">
              <h4 class="mb-0 font-weight-bold">Course List</h4>
            </div>
            <div class="col-md-3">
              <div class="d-flex justify-content-between align-items-center gap-2">
                <div class="w-100">
                  <b-form-input
                    class="w-full"
                    v-model="filters.search"
                    placeholder="Search Here..."
                    @keyup.enter="onSearch"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-outline-danger position-relative d-flex flex-row gap-1"
                    @click="showFilterModal"
                    style="height: 27.88px"
                  >
                    <span class="fe fe-sliders"></span> Filter
                    <span
                      v-if="activeFiltersCount > 0"
                      class="badge bg-danger position-absolute top-0 start-100 translate-middle rounded-pill"
                      style="font-size: 0.65rem; padding: 0.2rem 0.4rem"
                    >
                      {{ activeFiltersCount }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeFiltersCount > 0" class="mb-3">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <small class="text-muted">Active Filters:</small>

              <span v-if="filters.training_scope" class="badge bg-light text-dark border">
                Training Scope: {{ filters.training_scope }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_scope')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_category" class="badge bg-light text-dark border">
                Training Category: {{ filters.training_category === 'mandatory' ? 'Mandatory' : 'Development' }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_category')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.coaching_type_id" class="badge bg-light text-dark border">
                Coaching Type: {{ filters.coaching_type_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('coaching_type_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.platform" class="badge bg-light text-dark border">
                Platform: {{ filters.platform }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('platform')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_method_id" class="badge bg-light text-dark border">
                Training Method ID: {{ filters.training_method_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_method_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_type" class="badge bg-light text-dark border">
                Training Type: {{ filters.training_type }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_type')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.competency_id" class="badge bg-light text-dark border">
                Competency: {{ filters.competency_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('competency_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <button type="button" class="btn btn-sm btn-link text-danger p-0 ms-2" @click="resetFilters">Clear All</button>
            </div>
          </div>

          <CardGridStructured
            v-if="filters.learning_type === 'structured_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
            @open-upload-certificate-modal="showUploadCertificateModal"
            @download-certificate="downloadCertificate"
          />

          <CardGridSocial
            v-if="filters.learning_type === 'social_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
            @open-upload-presentation-modal="showUploadPresentationModal"
          />

          <CardGridExperiential
            v-if="filters.learning_type === 'experiential_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
          />

          <StructuredLearningModal
            v-if="filters.learning_type === 'structured_learning'"
            :modal-id="'structured-learning-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            @submit-enrollment="submitEnrollment"
            @open-upload-certificate-modal="showUploadCertificateModal"
            @close="showModal = false"
          />

          <SocialLearningModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'social-learning-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            @close="showModal = false"
            @open-update-summary-modal="showUpdateSummaryModal"
            @open-schedule-modal="showScheduleModal"
          />

          <ExperientialLearningModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'experiential-learning-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            @close="showModal = false"
            @open-update-activity-modal="showUpdateActivityModal"
          />

          <modal-filter
            v-model="filters"
            @apply-filters="applyFilters"
            @reset-filters="resetFilters"
            :training-type-options="trainingTypes"
            :competency-options="competencies"
            :training-method-options="trainingMethods"
            :learning-type="filters.learning_type"
          />

          <UploadCertificateModal
            v-if="filters.learning_type === 'structured_learning'"
            :modal-id="'upload-certificate-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            @upload-certificate="uploadCertificate"
          />

          <UpdateSummaryModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'update-summary-modal'"
            :data="summaryData"
            :is-loading="isUpdateSummaryLoading"
            @update-summary="updateSummary"
          />

          <ScheduleModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'schedule-modal'"
            :data="modalData"
            :is-loading="isAddScheduleLoading"
            @submit-schedule="submitSchedule"
            @close="showModal = false"
          />

          <UploadPresentationModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'upload-presentation-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            @upload-presentation="uploadPresentation"
          />

          <ActivityModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'update-activity-modal'"
            :data="activityData"
            :is-loading="isUpdateActivityLoading"
            @update-activity="updateActivity"
            @open-add-progress="showAddProgressModal"
          />

          <ProgressActivityModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'progress-activity-modal'"
            :data="activityData"
            :is-loading="isAddProgressLoading"
            @close="showModal = false"
            @submit-progress="submitProgress"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logic from "./main.logic";
import Header from "./components/header.vue";
import Summary from "./components/summary.vue";
import CardGridStructured from "./components/card-grid-structured.vue";
import StructuredLearningModal from "./modal/structured-learning-modal.vue";
import ModalFilter from "./modal/filter-modal.vue";
import UploadCertificateModal from "./modal/upload-certificate-modal.vue";
import Tabs from "./components/tabs.vue";
import CardGridSocial from "./components/card-grid-social.vue";
import SocialLearningModal from "./modal/social-learning-modal.vue";
import UpdateSummaryModal from "./modal/summary-modal.vue";
import ScheduleModal from "./modal/schedule-modal.vue";
import UploadPresentationModal from "./modal/upload-presentation-modal.vue";
import CardGridExperiential from "./components/card-grid-experiential.vue";
import ExperientialLearningModal from "./modal/experiential-learning-modal.vue";
import ActivityModal from "./modal/activity-modal.vue";
import ProgressActivityModal from "./modal/progress-activity-modal.vue";

export default {
  name: "IdpCourseCompletionPage",
  components: {
    Header,
    CardGridStructured,
    CardGridSocial,
    Summary,
    StructuredLearningModal,
    SocialLearningModal,
    ModalFilter,
    UploadCertificateModal,
    UpdateSummaryModal,
    UploadPresentationModal,
    ScheduleModal,
    CardGridExperiential,
    ExperientialLearningModal,
    ActivityModal,
    Tabs,
    ProgressActivityModal,
  },
  mixins: [logic],
};
</script>
